import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Readiness rubric configuration surface (T-0082 / E08-F01).
 *
 * Mirrors `GetRubricResult` from `apps/api/src/services/readiness-rubric.ts`.
 * Health & Readiness reads it for a criterion's detail (its rubric entry
 * and cross-module sources) — criteria, weights, thresholds, AI-proposed
 * provenance.
 *
 * The schema is intentionally `passthrough()` on cross_module_sources so a
 * future widening of the source-entity shape doesn't break the rubric
 * editor — the editor only inspects the array length to surface "this
 * criterion has live cross-module sources" hint.
 */

export const ProvenanceSchema = z.enum([
  'ai_proposed',
  'consultant_confirmed',
  'consultant_authored',
]);
export type RubricProvenance = z.infer<typeof ProvenanceSchema>;

export const ThresholdsSourceSchema = z.enum(['methodology_default', 'engagement_override']);
export type ThresholdsSource = z.infer<typeof ThresholdsSourceSchema>;

export const RubricScoreTypeSchema = z.enum(['numeric_0_10', 'pass_fail', 'ryg']);
export type RubricScoreType = z.infer<typeof RubricScoreTypeSchema>;

export const RubricCriterionSchema = z.object({
  id: z.string(),
  category: z.string(),
  name: z.string(),
  weight: z.number(),
  score_type: RubricScoreTypeSchema,
  pass_threshold: z.number().nullable(),
  cross_module_sources: z.array(z.unknown()),
  display_order: z.number(),
  provenance: ProvenanceSchema,
  is_active: z.boolean(),
  override_record_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type RubricCriterion = z.infer<typeof RubricCriterionSchema>;

export const ReadinessRubricSchema = z.object({
  rubric_config_id: z.string(),
  engagement_id: z.string(),
  go_threshold: z.number(),
  conditional_threshold: z.number(),
  thresholds_source: ThresholdsSourceSchema,
  is_active: z.boolean(),
  locked_at: z.string().nullable(),
  locked_by_user_id: z.string().nullable(),
  criteria: z.array(RubricCriterionSchema),
});
export type ReadinessRubric = z.infer<typeof ReadinessRubricSchema>;

export function getReadinessRubric(engagementId: string): Promise<ReadinessRubric> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/rubric`,
    ReadinessRubricSchema,
  );
}

/**
 * Live rubric query. `staleTime: 60s` — the rubric is read often (every
 * scorecard refresh) but mutates rarely (one Lead-confirmation per
 * engagement + occasional weight overrides). The Criteria editor invalidates
 * `['readiness', 'rubric', engagementId]` after PATCH/POST.
 */
export function useReadinessRubricQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['readiness', 'rubric', engagementId] as const,
    queryFn: () => getReadinessRubric(engagementId!),
    enabled: Boolean(engagementId),
    staleTime: 60_000,
  });
}

// ===========================================================================
// Criterion patch mutation (T-0082 / T-0114) — surfaces the
// PATCH /api/v1/engagements/:engagement_id/readiness/criteria/:criterion_id
// endpoint to the rubric editor modal. The editor sends a structured
// override blob with each saved weight change so the audit trail captures
// the consultant rationale.
// ===========================================================================

export interface PatchCriterionOverride {
  tag: string;
  rationale: string;
}

export interface PatchCriterionBody {
  weight?: number;
  pass_threshold?: number | null;
  override?: PatchCriterionOverride;
}

export interface PatchCriterionInput {
  engagementId: string;
  criterionId: string;
  body: PatchCriterionBody;
}

const PatchCriterionResponseSchema = z.object({ ok: z.boolean().optional() }).passthrough();

export function patchRubricCriterion(input: PatchCriterionInput): Promise<unknown> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(input.engagementId)}/readiness/criteria/${encodeURIComponent(input.criterionId)}`,
    PatchCriterionResponseSchema,
    { method: 'PATCH', body: input.body },
  );
}

export function usePatchRubricCriterionMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<PatchCriterionInput, 'engagementId'>) => {
      if (!engagementId) {
        return Promise.reject(new Error('engagementId required'));
      }
      return patchRubricCriterion({ engagementId, ...input });
    },
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['readiness', 'rubric', engagementId] });
      }
    },
  });
}

// ===========================================================================
// T-0126 editor extensions — add / remove criterion + threshold PATCH.
// Land alongside the T-0114 editor modals so consultants can manage the
// rubric mid-engagement without re-running the setup confirm flow.
// ===========================================================================

export interface AddCriterionBody {
  category: string;
  name: string;
  weight: number;
  score_type: RubricScoreType;
  pass_threshold?: number | null;
  display_order?: number;
}

const AddCriterionResponseSchema = z.object({
  criterion_id: z.string().uuid(),
});

export function addRubricCriterion(
  engagementId: string,
  body: AddCriterionBody,
  idempotencyKey?: string,
): Promise<{ criterion_id: string }> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/rubric/criteria`,
    AddCriterionResponseSchema,
    {
      method: 'POST',
      body,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useAddRubricCriterionMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }: { body: AddCriterionBody; idempotencyKey?: string }) => {
      if (!engagementId) return Promise.reject(new Error('engagementId required'));
      return addRubricCriterion(engagementId, body, idempotencyKey);
    },
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['readiness', 'rubric', engagementId] });
      }
    },
  });
}

const DeleteCriterionResponseSchema = z.object({ ok: z.boolean().optional() }).passthrough();

export function deleteRubricCriterion(engagementId: string, criterionId: string): Promise<unknown> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/rubric/criteria/${encodeURIComponent(
      criterionId,
    )}`,
    DeleteCriterionResponseSchema,
    { method: 'DELETE' },
  );
}

export function useDeleteRubricCriterionMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ criterionId }: { criterionId: string }) => {
      if (!engagementId) return Promise.reject(new Error('engagementId required'));
      return deleteRubricCriterion(engagementId, criterionId);
    },
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['readiness', 'rubric', engagementId] });
      }
    },
  });
}

export interface PatchRubricThresholdsBody {
  go_threshold?: number;
  conditional_threshold?: number;
}

const PatchRubricThresholdsResponseSchema = z.object({ ok: z.boolean().optional() }).passthrough();

export function patchRubricThresholds(
  engagementId: string,
  body: PatchRubricThresholdsBody,
): Promise<unknown> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/rubric`,
    PatchRubricThresholdsResponseSchema,
    { method: 'PATCH', body },
  );
}

export function usePatchRubricThresholdsMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchRubricThresholdsBody) => {
      if (!engagementId) return Promise.reject(new Error('engagementId required'));
      return patchRubricThresholds(engagementId, body);
    },
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['readiness', 'rubric', engagementId] });
      }
    },
  });
}
